import { useMemo, useState } from "react";
import { X } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";
import { classerEcarts } from "../../lib/speakerDivergence";
import { SpeakerDivergenceModal } from "./SpeakerDivergenceModal";

/**
 * Tâche 1399 (Eric 2026-09-30) — Tous les écarts d'une vidéo, d'un coup.
 *
 * Avant, chaque désaccord s'ouvrait depuis SON badge : sur une vidéo à douze
 * locuteurs, il fallait douze allers-retours pour savoir lequel corriger en
 * premier — et rien ne disait lequel était le pire.
 *
 * Le tableau montre le TEMPS DE PAROLE par source, pas le nombre de tours :
 * c'est la seule grandeur comparable entre deux diariseurs qui ne découpent
 * pas aux mêmes endroits (décision de la tâche 1397). Le classement va du
 * pire au meilleur.
 *
 * La comparaison côte à côte reste la fenêtre de 1398 : elle s'ouvre PAR
 * DESSUS ce tableau, et la refermer ramène à la liste. Remplacer le tableau
 * obligerait à le rouvrir entre chaque locuteur, c'est-à-dire à refaire
 * exactement ce qu'Eric demande d'éviter.
 */

const mmss = (s) => {
  const t = Math.max(0, Math.round(Number(s) || 0));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

const SOURCES = ["whisper", "fireflies", "merged"];

export function EcartsGroupesModal({
  open,
  onClose,
  statsParNom,
  sourcesPresentes,
  seuilPct = 50,
  sourceLabels,
  segmentsBySource,
  onSeek,
}) {
  const { tr, lang } = useLang();
  const [compare, setCompare] = useState(null);

  const lignes = useMemo(
    () => classerEcarts(statsParNom, sourcesPresentes, seuilPct),
    [statsParNom, sourcesPresentes, seuilPct],
  );

  if (!open) return null;

  const nbDepasse = lignes.filter((l) => l.depasse).length;

  return (
    <>
      <div
        className="fixed inset-0 z-[55] bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="ecarts-groupes-overlay"
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="ecarts-groupes-modal"
        >
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-zinc-200">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-zinc-900" data-testid="ecarts-groupes-titre">
                {tr("Tous les écarts entre sources", "All gaps between sources")}
              </h3>
              <p className="text-xs text-zinc-600 mt-1" data-testid="ecarts-groupes-resume">
                {lang === "fr"
                  ? `${nbDepasse} locuteur(s) au-delà du seuil de ${seuilPct} % sur ${lignes.length}. Classés du pire au meilleur : le temps de parole est la seule mesure comparable entre deux découpages différents.`
                  : `${nbDepasse} speaker(s) beyond the ${seuilPct}% threshold out of ${lignes.length}. Sorted worst first: speaking time is the only measure comparable across two different segmentations.`}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-700 shrink-0"
              aria-label={tr("Fermer", "Close")}
              data-testid="ecarts-groupes-close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="overflow-auto flex-1">
            <table className="w-full text-xs" data-testid="ecarts-groupes-table">
              <thead className="sticky top-0 bg-zinc-50">
                <tr className="text-left text-[10px] font-semibold text-zinc-600 border-b border-zinc-200">
                  {/* À 390 px le tableau défile horizontalement : sans
                      colonne collée, le NOM sort de l'écran et il ne reste
                      que des chiffres sans sujet. */}
                  <th className="px-4 py-2 sticky left-0 bg-zinc-50 z-10">{tr("Locuteur", "Speaker")}</th>
                  {SOURCES.map((src) => (
                    <th key={src} className="px-3 py-2 text-right" data-testid={`ecarts-groupes-col-${src}`}>
                      {sourceLabels[src]}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right">{tr("Écart", "Gap")}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {lignes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-zinc-500" data-testid="ecarts-groupes-vide">
                      {tr("Aucun locuteur sur cette vidéo.", "No speaker on this video.")}
                    </td>
                  </tr>
                )}
                {lignes.map((l, i) => (
                  <tr
                    key={l.name}
                    className={`border-b border-zinc-100 ${l.depasse ? "bg-red-50" : ""}`}
                    data-testid={`ecarts-groupes-row-${l.name}`}
                    data-rang={i}
                    data-depasse={l.depasse ? "true" : "false"}
                    data-pct={l.pct}
                  >
                    <td
                      className={`px-4 py-2 font-medium text-zinc-800 sticky left-0 z-10 ${
                        l.depasse ? "bg-red-50" : "bg-white"
                      }`}
                    >
                      {l.name}
                    </td>
                    {SOURCES.map((src) => {
                      const st = (statsParNom || {})[l.name] || {};
                      const s = st[src];
                      return (
                        <td
                          key={src}
                          className="px-3 py-2 text-right tabular-nums text-zinc-700 whitespace-nowrap"
                          data-testid={`ecarts-groupes-cell-${l.name}-${src}`}
                        >
                          {s && s.turns ? mmss(s.seconds) : "—"}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right tabular-nums">
                      {l.pair ? (
                        <span className={l.depasse ? "font-bold text-red-700" : "text-zinc-600"}>
                          {l.pct} %
                        </span>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {l.pair ? (
                        <button
                          type="button"
                          onClick={() => setCompare({ name: l.name, pair: l.pair })}
                          className="text-[11px] font-semibold text-[#002FA7] hover:underline"
                          data-testid={`ecarts-groupes-comparer-${l.name}`}
                        >
                          {tr("Comparer", "Compare")}
                        </button>
                      ) : (
                        <span
                          className="text-[10px] text-zinc-500"
                          data-testid={`ecarts-groupes-absent-${l.name}`}
                        >
                          {l.absentes.length
                            ? (lang === "fr"
                                ? `absent de ${l.absentes.map((k) => sourceLabels[k]).join(", ")}`
                                : `not in ${l.absentes.map((k) => sourceLabels[k]).join(", ")}`)
                            : (lang === "fr" ? "rien à comparer" : "nothing to compare")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-zinc-200 text-[11px] text-zinc-500">
            {lang === "fr"
              ? "Un écart élevé ne signifie pas toujours une erreur : deux diariseurs peuvent découper la même parole autrement. Ouvrir la comparaison est le seul moyen de trancher."
              : "A high gap does not always mean an error: two diarizers may cut the same speech differently. Opening the comparison is the only way to decide."}
          </div>
        </div>
      </div>

      <SpeakerDivergenceModal
        open={Boolean(compare)}
        onClose={() => setCompare(null)}
        name={compare?.name}
        pair={compare?.pair}
        sourceLabels={sourceLabels}
        segmentsBySource={segmentsBySource}
        stats={compare ? (statsParNom || {})[compare.name] : null}
        onSeek={onSeek}
      />
    </>
  );
}

export default EcartsGroupesModal;
